import { Box, Button, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";

import type { SalesCapabilityProposal } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";
import type { SxStyle } from "../leads/heatStyles.js";

const panelSx: SxStyle = {
  display: "grid",
  gap: 1.5,
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: (t: Theme) => t.shadows[1],
  p: 2.5,
};

/**
 * Sales ▸ Knowledge — the agent's self-expanding solution-fit map.
 *
 * The agent reads XOP's capability docs from the KB and PROPOSES new
 * capability→vertical fits (markets a human didn't pre-list, e.g. car-parts →
 * Hoot-n-Holler). The operator approves/rejects here; approved ones become live
 * discovery angles on the next refill. Below the proposals, the CURATED catalog
 * shows what solution-led discovery already hunts (read-only context).
 *
 * Reads GET /v2/capabilities/proposals + /capabilities/catalog.
 */
export function CapabilityMap(): JSX.Element {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();

  const proposals = useQuery({
    queryKey: ["sales", "capabilities", "proposals"],
    queryFn: salesApi.listCapabilityProposals,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  const catalog = useQuery({
    queryKey: ["sales", "capabilities", "catalog"],
    queryFn: salesApi.getCapabilityCatalog,
    staleTime: 10 * 60_000,
  });

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      action === "approve"
        ? salesApi.approveCapabilityProposal(id)
        : salesApi.rejectCapabilityProposal(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["sales", "capabilities", "proposals"] }),
  });

  const all = proposals.data?.proposals ?? [];
  const pending = all.filter((p) => p.status === "pending");
  const approved = all.filter((p) => p.status === "approved");
  const entries = catalog.data?.entries ?? [];

  return (
    <Box component="section" sx={panelSx} aria-label="Capability map">
      <Box component="header" sx={{ display: "grid", gap: 0.25 }}>
        <Typography component="h3" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          <Box component="span" aria-hidden>🎯</Box> Capability map — the agent&apos;s solution-fit ideas
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          The agent reads our product docs and proposes non-obvious markets where
          a capability fits. Approve one and it becomes a live discovery angle.
        </Typography>
      </Box>

      {/* Pending proposals — the operator's review queue. */}
      {pending.length > 0 ? (
        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>
            {`Proposed fits — your review (${pending.length})`}
          </Typography>
          <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
            {pending.map((p) => (
              <ProposalRow
                key={p.id}
                proposal={p}
                canEdit={canEdit}
                busy={decide.isPending}
                onDecide={(action) => decide.mutate({ id: p.id, action })}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          No new ideas waiting — the agent proposes a few every few hours as it
          re-reads the knowledge base.
        </Typography>
      )}

      {approved.length > 0 ? (
        <Box sx={{ display: "grid", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            {`Approved — now live discovery angles (${approved.length})`}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.625 }}>
            {approved.map((p) => (
              <Chip key={p.id} tone="good" title={`${p.capability} — ${p.use_case}`}>
                {p.vertical}
              </Chip>
            ))}
          </Box>
        </Box>
      ) : null}

      {/* Curated catalog — read-only context for what's already hunted. */}
      {entries.length > 0 ? (
        <Box sx={{ display: "grid", gap: 0.75, mt: 0.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            Already hunting — the built-in capability catalog
          </Typography>
          <Box sx={{ display: "grid", gap: 0.875 }}>
            {entries.map((e) => (
              <Box key={e.product} sx={{ display: "grid", gap: 0.375 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
                  {e.product}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {e.verticals.map((v) => (
                    <Chip key={v.vertical} tone="neutral" title={v.use_case}>
                      {v.vertical}
                    </Chip>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function ProposalRow({
  proposal: p,
  canEdit,
  busy,
  onDecide,
}: {
  proposal: SalesCapabilityProposal;
  canEdit: boolean;
  busy: boolean;
  onDecide: (action: "approve" | "reject") => void;
}): JSX.Element {
  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box sx={{ display: "grid", gap: 0.375, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, color: "text.primary" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{p.vertical}</Box>
          <Box component="span" sx={{ color: "text.disabled" }}> · </Box>
          <Box component="span" sx={{ color: "text.secondary" }}>{p.capability}</Box>
        </Typography>
        {p.use_case ? (
          <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: "text.primary" }}>{p.use_case}</Typography>
        ) : null}
        {p.rationale ? (
          <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: "text.disabled" }}>{p.rationale}</Typography>
        ) : null}
        {p.kb_source ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.125 }}>
            <Chip tone="neutral">KB: {p.kb_source}</Chip>
          </Box>
        ) : null}
      </Box>
      {canEdit ? (
        <Box sx={{ display: "flex", flexShrink: 0, gap: 0.75 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            disableElevation
            onClick={() => onDecide("approve")}
            disabled={busy}
            aria-label={`Approve fit: ${p.vertical}`}
            sx={{ fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5 }}
          >
            Approve
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => onDecide("reject")}
            disabled={busy}
            aria-label={`Reject fit: ${p.vertical}`}
            sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", px: 1.25, py: 0.5 }}
          >
            Reject
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

type Tone = "good" | "neutral";

function Chip({
  children,
  tone = "neutral",
  title,
}: {
  children: React.ReactNode;
  tone?: Tone;
  title?: string;
}): JSX.Element {
  return (
    <Box
      component="span"
      title={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        border: 1,
        px: 0.875,
        py: 0.125,
        fontSize: 11,
        fontWeight: 500,
        color: (t: Theme) => (tone === "good" ? t.palette.success.main : t.palette.text.secondary),
        bgcolor: (t: Theme) =>
          tone === "good" ? alpha(t.palette.success.main, 0.1) : "surfaceSoft",
        borderColor: (t: Theme) =>
          tone === "good" ? alpha(t.palette.success.main, 0.4) : t.palette.divider,
      }}
    >
      {children}
    </Box>
  );
}
